
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, Link2, BadgeDollarSign, Settings, LineChart, AlertCircle } from "lucide-react";
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { CommissionTable } from './CommissionTable';
import { AffiliateStats } from './AffiliateStats';

export function ReferralLinkGenerator() {
  const { session } = useAuthSession();
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    const fetchAffiliateData = async () => {
      setIsLoading(true);
      setError(null);
      
      if (!session?.user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        console.log('Fetching affiliate data for user:', session.user.id);
        
        // Fetch affiliate status and referral code
        const { data: affiliateData, error: affiliateError } = await supabase
          .from('affiliates')
          .select('referral_code, status, id')
          .eq('user_id', session.user.id)
          .maybeSingle();
          
        if (affiliateError) {
          console.error('Error fetching affiliate data:', affiliateError);
          throw affiliateError;
        }
        
        if (affiliateData) {
          console.log('Found affiliate data:', affiliateData);
          setReferralCode(affiliateData.referral_code);
          
          // Generate referral link
          const baseUrl = window.location.origin;
          setReferralLink(`${baseUrl}/refer/${affiliateData.referral_code}`);
          
          if (affiliateData.status === 'approved') {
            // Fetch referral stats using the affiliate ID
            const { data: referralStats, error: referralStatsError } = await supabase
              .from('affiliate_referrals')
              .select('*')
              .eq('affiliate_id', affiliateData.id);
              
            if (referralStatsError) throw referralStatsError;
            
            console.log('Referral stats:', referralStats);
            
            // Set total referrals
            const total = referralStats ? referralStats.length : 0;
            setTotalReferrals(total);
            
            // For earnings, we'll use a fixed estimate since the subscription data might not be available
            // A more robust solution would be to store commission values in the affiliate_referrals table
            const estimatedEarningsPerReferral = 500; // ₹500 per referral as a placeholder
            setTotalEarnings(total * estimatedEarningsPerReferral);
          }
        }
      } catch (error: any) {
        console.error('Error fetching affiliate data:', error);
        setError(`Failed to fetch affiliate data: ${error.message}`);
        toast.error(`Failed to fetch affiliate data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAffiliateData();
  }, [session?.user?.id]);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopyButtonText('Copied!');
        toast.success('Referral link copied to clipboard!');
        setTimeout(() => {
          setCopyButtonText('Copy');
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy the referral link.');
      });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-2">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading affiliate dashboard...</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Error Loading Affiliate Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">
            <LineChart className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="commissions">
            <BadgeDollarSign className="h-4 w-4 mr-2" />
            Commissions
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4 pt-4">
          <AffiliateStats 
            totalReferrals={totalReferrals}
            totalEarnings={totalEarnings}
          />
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Referral Link</CardTitle>
              <CardDescription>
                Share this link with friends to earn commissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md bg-muted/20 text-sm"
                  />
                </div>
                <Button onClick={handleCopy}>
                  <Copy className="h-4 w-4 mr-2" />
                  {copyButtonText}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Link2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">1. Share your unique referral link</h4>
                  <p className="text-sm text-muted-foreground">
                    Share with your audience on social media, your website, or directly with friends
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Copy className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">2. Your referrals sign up</h4>
                  <p className="text-sm text-muted-foreground">
                    When someone clicks your link and creates an account, they're tracked as your referral
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <BadgeDollarSign className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">3. Earn commission</h4>
                  <p className="text-sm text-muted-foreground">
                    When your referral subscribes to a paid plan, you earn a commission for each payment
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="commissions" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Commission History</CardTitle>
              <CardDescription>
                Track earnings from your referrals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CommissionTable />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account Settings</CardTitle>
              <CardDescription>
                Manage your affiliate account settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Payment Information</h4>
                  <p className="text-sm text-muted-foreground">
                    You'll be able to add payment details soon to receive your commissions.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Marketing Materials</h4>
                  <p className="text-sm text-muted-foreground">
                    You'll be able to access marketing materials soon.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Notification Preferences</h4>
                  <p className="text-sm text-muted-foreground">
                    You'll be able to customize your notification preferences soon.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReferralLinkGenerator;
